/**
 * The assistant drawer — PLAN §4.2's `⌘J` panel, docked right.
 *
 * Docked and resizable rather than floating, because it is a *participant* in
 * the editor, not an overlay on it: the vitals rail has to stay visible while a
 * proposal is being reviewed, or the live preview is comparing against something
 * the user cannot see. The width is dragged and remembered.
 *
 * That is the treatment from `xl` (1280 px) and no earlier. It used to begin at
 * `md`, which made it a side panel on every tablet: the shell's row is nav plus
 * editor plus drawer, so a 420 px drawer beside a 240 px nav left an iPad Air
 * 112 px of recipe, and an iPad Pro in portrait less than nothing. Below `xl`
 * the drawer keeps the phone's behaviour — it covers the screen, and the header's
 * close button and ⌘J both dismiss it — because a conversation you can read is
 * worth more there than a column beside a recipe you cannot. What it covers
 * there is the *visible* viewport rather than the laid-out one: the drawer is
 * opened on a phone in order to type into it, and Safari both hides a strip
 * behind the keyboard and slides the visible box down the page to reveal the
 * field, so pinning either edge to the layout viewport puts the composer or
 * the close button out of reach.
 *
 * Three states before a conversation is possible, each shown rather than
 * silently disabled, and each saying something different because the fix is
 * different:
 *
 * - **Signed out.** The assistant reads the authoritative workspace, which only
 *   exists for an account, so the drawer invites sign-in.
 * - **Not configured.** No AI provider credential on the deployment; the notice
 *   names the variables, exactly as the sign-in page does for Google.
 * - **Out of messages.** The month's allowance is spent (PLAN §9). This one is
 *   driven by the server's own count — the 429 body, or `GET /api/ai/usage` on
 *   open — so it names the real number, the month it comes back, and (unless
 *   they are already on the top tier) the tier that carries a bigger one. The
 *   composer goes away with it: leaving a send button that is certain to be
 *   refused would be a worse answer than removing it.
 */

import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  ArrowUpIcon,
  CheckIcon,
  ClipboardCopyIcon,
  KeyRoundIcon,
  LogInIcon,
  MessageSquarePlusIcon,
  SquareIcon,
  WrenchIcon,
  XIcon,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clampDrawerWidth, useAssistant } from '@/ai/assistant-context';
import type { AiThreadSubject } from '@/ai/client';
import { fetchThreads, subjectOfThreadId } from '@/ai/client';
import type { AiThreadEntry, AiThreadSummary } from '@/ai/protocol';
import { useSession } from '@/auth/session-context';
import { AllowanceSpent } from '@/components/assistant/allowance-spent';
import { Markdown } from '@/components/assistant/markdown';
import { ProposalCard } from '@/components/assistant/proposal-card';
import { AssistantOrb } from '@/components/assistant-orb';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { userContent } from '@/lib/posthog';
import { cn } from '@/lib/utils';

/**
 * The header's second line, from the thread's own subject.
 *
 * It used to read "This recipe" or "Workspace conversation" off `recipeId`
 * alone, which told a brewer on a batch or a blending session that they were in
 * the workspace conversation when they were not.
 */
const SUBTITLE: Readonly<Record<AiThreadSubject['kind'], string>> = {
  recipe: 'This recipe',
  batch: 'This batch',
  blend: 'This blend',
  global: 'Workspace conversation',
};

/**
 * What the composer invites, per subject (PLAN-UX-PASS F3).
 *
 * It read "Ask about this recipe" on every thread, including the batch and
 * blend threads the header above it was already naming correctly — so a brewer
 * on a batch page was told two different things about where they were, two
 * lines apart. A placeholder is a label, so these stay terse; what they have to
 * do is name the same subject {@link SUBTITLE} does.
 */
const PLACEHOLDER: Readonly<Record<AiThreadSubject['kind'], string>> = {
  recipe: 'Ask about this recipe, or ask for a change…',
  batch: 'Ask about this batch, or what to do next…',
  blend: 'Ask about this blend, or what a change would do…',
  global: 'Ask about your brewing, or start a recipe…',
};

/**
 * What an empty thread offers to ask, per subject (PLAN-UX-PASS F3).
 *
 * Every one of these is a question the assistant can actually answer with the
 * tools it has, which is the whole constraint on the list: a prompt that comes
 * back "I can't see that" is worse than no prompt. The batch three are
 * `get_batch`, which carries the fermentation curve folded to its terminal
 * gravity and the days it took, the frozen prediction beside what was measured,
 * and attenuation against the yeast's published band. The blend three are
 * `get_blend`, whose evaluation is ABV, the priming dose and what is already
 * carbonating — and `calculate`, which will run a fruit addition as a
 * hypothetical without saving it.
 */
const STARTER_PROMPTS: Readonly<Record<AiThreadSubject['kind'], readonly string[]>> = {
  recipe: [
    'Make this hoppier',
    'Why is my predicted FG so high?',
    'Is this to style, and what is furthest out?',
  ],
  batch: [
    'Is this done fermenting?',
    'How do my numbers compare with the recipe?',
    'Did the yeast attenuate as far as it should have?',
  ],
  blend: [
    'What does this blend come to for ABV?',
    'How much priming sugar does this need?',
    'What would adding fruit do to it?',
  ],
  global: [
    'Design a 5 gal hazy IPA around Citra and Nelson',
    'What is a good water profile for a stout?',
  ],
};

/**
 * The line a journal page gets, and only a journal page (PLAN-UX-PASS F3).
 *
 * A journal sets no subject, so the drawer opens the workspace conversation
 * there — and a brewer looking at an entry they just wrote has every reason to
 * think the assistant is looking at it too. Nowhere else the workspace thread
 * opens has a document on screen worth mistaking for one, so nowhere else says
 * this.
 */
const JOURNAL_NOTE =
  'This is your workspace conversation. It can’t read the journal in front of you, so ask about your recipes, batches and blends.';

/**
 * What the confirmation says is about to be lost, per subject.
 *
 * The global thread drops the second sentence: nothing was ever accepted into
 * anything from it, so promising that it survives would be a sentence about a
 * thing that does not exist.
 */
/** What kind of conversation a row in the recent list is, in one lowercase word. */
const THREAD_LABEL: Readonly<Record<AiThreadSubject['kind'], string>> = {
  recipe: 'recipe',
  batch: 'batch',
  blend: 'blend',
  global: 'workspace',
};

const CLEAR_BODY: Readonly<Record<AiThreadSubject['kind'], string>> = {
  recipe:
    'We delete what you and the assistant have said about this recipe. Anything you accepted is already in the recipe and stays there.',
  batch:
    'We delete what you and the assistant have said about this batch. Anything you accepted is already saved and stays there.',
  blend:
    'We delete what you and the assistant have said about this blend. Anything you accepted is already saved and stays there.',
  global: 'We delete what you and the assistant have said in the workspace conversation.',
};

export function AssistantDrawer() {
  const assistant = useAssistant();
  if (!assistant.open) return null;
  return <DrawerBody />;
}

function DrawerBody() {
  const assistant = useAssistant();
  const session = useSession();
  const navigate = useNavigate();
  const scroller = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [clearing, setClearing] = useState(false);

  // What this thread is about, read back off its derived id — the same fact the
  // subtitle states, the dialog names, and `clearThread` deletes.
  const subject = subjectOfThreadId(assistant.threadId);

  const signedIn = session.status === 'authenticated';
  const mode = session.config.ai.mode;
  // The server's count, not the client's: set by `GET /api/ai/usage` on open and
  // replaced by the 429's copy when a send is refused mid-conversation.
  const allowance = assistant.allowance;
  const exhausted = allowance !== null && allowance.used >= allowance.limit;

  // Stay pinned to the bottom as the answer streams in. The transcript is the
  // trigger rather than an input, which is what the suppression is saying.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll follows new content
  useLayoutEffect(() => {
    const element = scroller.current;
    if (element === null) return;
    element.scrollTop = element.scrollHeight;
  }, [assistant.entries, assistant.liveText]);

  // What the drag and the docked width are measured against. Tracked as state
  // rather than read at render, because a window that is resized under an open
  // drawer has to re-clamp: React has no other reason to re-render for it.
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth,
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // The remembered width, fitted to this window. `assistant.width` is left
  // exactly as the brewer set it — see `clampDrawerWidth`.
  const width = clampDrawerWidth(assistant.width, viewportWidth);

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      assistant.setWidth(clampDrawerWidth(window.innerWidth - event.clientX, window.innerWidth));
    },
    [assistant],
  );

  useEffect(() => {
    if (!dragging) return;
    const stop = () => setDragging(false);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stop);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stop);
    };
  }, [dragging, onPointerMove]);

  return (
    <aside
      aria-label="Assistant"
      data-slot="assistant-drawer"
      style={{ ['--assistant-width' as string]: `${width}px` }}
      className="fixed inset-x-0 top-(--visual-viewport-top,0px) bottom-(--keyboard-inset,0px) z-50 flex min-h-0 w-full flex-col border-l border-border bg-card xl:relative xl:inset-auto xl:z-auto xl:h-full xl:w-(--assistant-width) xl:bg-card/40 xl:shrink-0"
    >
      {/* A resize gutter, and only where there is room to resize into: it
          appears with the docked treatment at `xl` and not before, because
          below that the drawer covers the screen and there is no column beside
          it to trade width with.

          Eight pixels and a `col-resize` cursor is a mouse affordance. A coarse
          pointer gets 24 instead — twelve either side of the border, which is
          exactly the drawer's own `px-3`, so the wider target lands on padding
          rather than swallowing taps on the content behind it. `touch-none`
          keeps a drag from scrolling the page out from under itself. */}
      <div
        aria-hidden="true"
        onPointerDown={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        className={cn(
          'absolute inset-y-0 -left-1 z-10 hidden w-2 touch-none cursor-col-resize xl:block',
          'pointer-coarse:-left-3 pointer-coarse:w-6',
          dragging && 'bg-primary/20',
        )}
      />

      <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
        <AssistantOrb className="size-4" live={assistant.streaming} />
        <div className="min-w-0 flex-1 leading-tight">
          <p className="text-xs font-semibold">Assistant</p>
          <p className="truncate text-[11px] text-muted-foreground">
            {SUBTITLE[subject.kind]}
            {mode === 'fake' && ' · offline dev model'}
          </p>
        </div>
        {/* Only with something to start over from: on an empty thread it would
            be the first thing a new brewer wondered about, and it would do
            nothing. Disabled mid-turn for the reason `clearThread` refuses —
            an answer still arriving would be written into a deleted thread. */}
        {assistant.entries.length > 0 && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Start a new conversation"
            className="text-muted-foreground"
            disabled={assistant.streaming}
            onClick={() => setClearing(true)}
          >
            <MessageSquarePlusIcon />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="Close the assistant"
          className="text-muted-foreground"
          onClick={() => assistant.setOpen(false)}
        >
          <XIcon />
        </Button>
      </header>

      {/* The same register as deleting a batch or a listing: the loss named
          first, and what survives it named second. */}
      <ConfirmDialog
        open={clearing}
        onOpenChange={setClearing}
        title="Start a new conversation?"
        description={CLEAR_BODY[subject.kind]}
        confirmLabel="Delete and start again"
        destructive
        onConfirm={() => assistant.clearThread()}
      />

      <div ref={scroller} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
        {!signedIn ? (
          <Notice
            icon={<LogInIcon className="size-4" aria-hidden="true" />}
            title="Sign in to use the assistant"
            body="The assistant reads your saved recipes and equipment from your account, so you will need one. Signed out, everything else in Beerwright still works locally."
          />
        ) : mode === 'off' ? (
          <Notice
            icon={<KeyRoundIcon className="size-4" aria-hidden="true" />}
            title="The assistant isn’t set up on this deployment yet"
            body={
              <>
                To switch it on, set <code className="font-mono">ANTHROPIC_API_KEY</code> in{' '}
                <code className="font-mono">apps/web/.dev.vars</code> (or as Worker secrets). For
                OpenAI instead, set <code className="font-mono">OPENAI_API_KEY</code> plus{' '}
                <code className="font-mono">BEERWRIGHT_AI_MODEL</code> with{' '}
                <code className="font-mono">BEERWRIGHT_AI_PROVIDER=openai</code>. Or set{' '}
                <code className="font-mono">BEERWRIGHT_FAKE_AI=1</code> for the offline development
                model.
              </>
            }
          />
        ) : (
          <>
            {assistant.entries.length === 0 && <EmptyState subject={subject} />}
            {assistant.entries.map((entry, index) => (
              <Entry
                key={entry.id}
                entry={entry}
                onCreated={(recipeId) => {
                  void navigate({ to: '/recipes/$recipeId', params: { recipeId } });
                }}
                // Only the *last* failure is worth retrying, and only if there
                // is a question behind it: an older one has already been
                // answered or asked again, and a button on it would re-ask
                // something from the middle of a conversation that has moved
                // on.
                onRetry={retryOf(assistant.entries, index, assistant.send)}
              />
            ))}
            {assistant.liveText !== '' && (
              <Markdown text={assistant.liveText} className="text-foreground" />
            )}
            {assistant.thinking && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <AssistantOrb className="size-4" live />
                Thinking…
              </p>
            )}
            {exhausted && allowance !== null && <AllowanceSpent allowance={allowance} />}
            {assistant.requestError !== null && assistant.requestError.code !== 'rate_limited' && (
              <Notice
                icon={<AlertTriangleIcon className="size-4" aria-hidden="true" />}
                title="We could not send that"
                body={assistant.requestError.message}
                variant="warning"
                // The send never reached the server, so the question is still
                // on screen as the optimistic entry it was drawn as — and that
                // is the copy this re-sends. The retry adopts it rather than
                // adding a second bubble saying the same thing.
                action={retryOf(assistant.entries, assistant.entries.length, assistant.send)}
              />
            )}
          </>
        )}
      </div>

      {signedIn && mode !== 'off' && !exhausted && <Composer subject={subject} />}
    </aside>
  );
}

function Composer({ subject }: { readonly subject: AiThreadSubject }) {
  const assistant = useAssistant();
  const input = useRef<HTMLTextAreaElement>(null);

  // Focus on open, and whenever a prefill arrives from an explain popover.
  useEffect(() => {
    input.current?.focus();
  }, []);

  return (
    <form
      className="shrink-0 border-t border-border p-2"
      onSubmit={(event) => {
        event.preventDefault();
        assistant.send(assistant.draft);
      }}
    >
      <div className="flex items-end gap-2">
        <Textarea
          ref={input}
          rows={2}
          value={assistant.draft}
          disabled={assistant.busy}
          placeholder={PLACEHOLDER[subject.kind]}
          aria-label="Message the assistant"
          className="max-h-40 min-h-[2.5rem] resize-none text-sm"
          onChange={(event) => assistant.setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.shiftKey) return;
            event.preventDefault();
            assistant.send(assistant.draft);
          }}
        />
        {/* Mid-turn the submit button *becomes* Stop rather than sitting there
            disabled with a spinner in it: the one control the composer has is
            the one thing a brewer wants at that moment, and a disabled button
            beside an answer they have already read enough of is the worst of
            both. A stop still costs the message — see `stop` in the context. */}
        {assistant.streaming ? (
          <Button
            type="button"
            size="icon-sm"
            variant="secondary"
            aria-label="Stop the assistant"
            onClick={() => assistant.stop()}
          >
            <SquareIcon />
          </Button>
        ) : (
          <Button
            type="submit"
            size="icon-sm"
            aria-label="Send"
            disabled={assistant.busy || assistant.draft.trim() === ''}
          >
            <ArrowUpIcon />
          </Button>
        )}
      </div>
      <p className="mt-1 px-1 text-[10px] text-muted-foreground">
        {/* A composer waiting on an answer this page cannot see says what it is
            waiting for, rather than sitting there greyed out for no reason. */}
        {assistant.busy && !assistant.streaming
          ? 'We’re still answering in your other conversation. You can ask here once that’s done.'
          : 'Enter sends · Shift+Enter for a new line · nothing changes until you accept'}
      </p>
    </form>
  );
}

/**
 * The question a failure was an answer to, and the way to ask it again.
 *
 * `index` is where to look back from — the failed notice's own position, or the
 * length of the transcript for a send that never left the browser. The nearest
 * preceding `user` entry is the question; `undefined` when there is none to
 * re-ask, so a caller can simply not draw the button.
 */
function retryOf(
  entries: readonly AiThreadEntry[],
  index: number,
  send: (message: string) => void,
): (() => void) | undefined {
  const notice = entries[index];
  // Every caller but the request-error notice has to be looking at the last
  // entry, and it has to be a failure.
  if (notice !== undefined && !(notice.type === 'notice' && notice.reason === 'failed')) {
    return undefined;
  }
  if (notice !== undefined && index !== entries.length - 1) return undefined;
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const entry = entries[cursor];
    if (entry?.type === 'user') return () => send(entry.text);
  }
  return undefined;
}

function Entry({
  entry,
  onCreated,
  onRetry,
}: {
  readonly entry: AiThreadEntry;
  readonly onCreated: (recipeId: string) => void;
  /** Ask the question again, on the last failure of a conversation. */
  readonly onRetry?: (() => void) | undefined;
}) {
  switch (entry.type) {
    case 'user':
      return (
        <p
          {...userContent}
          className="self-end rounded-lg rounded-br-sm bg-primary px-3 py-1.5 text-sm text-primary-foreground"
        >
          {entry.text}
        </p>
      );
    case 'assistant':
      return (
        <div className="group flex flex-col items-start gap-0.5">
          <Markdown text={entry.text} />
          <CopyAnswer text={entry.text} />
        </div>
      );
    case 'activity':
      return (
        <p
          data-slot="tool-chip"
          className={cn(
            'flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]',
            entry.failed
              ? 'border-warning/40 bg-warning/5 text-warning'
              : 'border-border bg-muted/50 text-muted-foreground',
          )}
          title={entry.tool}
        >
          <WrenchIcon className="size-2.5 shrink-0" aria-hidden="true" />
          {/* Tool summaries name the recipe they acted on. */}
          <span {...userContent}>{entry.summary}</span>
        </p>
      );
    case 'proposal':
      return <ProposalCard proposal={entry.proposal} onCreated={onCreated} />;
    // A line, not a bubble: a withdrawn turn and a failed one are things that
    // happened *to* the conversation, and drawing either as something the
    // assistant said would be the drawer putting words in its mouth.
    case 'notice':
      return (
        <div className="flex flex-col items-start gap-1.5 px-1">
          <p className="text-[11px] text-muted-foreground italic">{entry.text}</p>
          {onRetry !== undefined && <RetryButton onRetry={onRetry} />}
        </div>
      );
  }
}

function EmptyState({ subject }: { readonly subject: AiThreadSubject }) {
  const assistant = useAssistant();
  const prompts = STARTER_PROMPTS[subject.kind];
  // The one thing here that is about the route rather than the thread. Both
  // halves are required: a journal is the page worth saying it on, and the
  // workspace thread is the only thread it can be true of. The trailing slash
  // is load-bearing — `/journals` itself is a list of journals, and a brewer
  // reading a list is not looking at an entry the assistant might have read.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const onJournal = subject.kind === 'global' && pathname.startsWith('/journals/');

  return (
    <>
      <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border px-3 py-4">
        <p className="flex items-center gap-2 text-xs font-medium">
          <AssistantOrb className="size-3.5" />
          Ask anything about your beer
        </p>
        <p className="text-[11px] leading-snug text-muted-foreground">
          It reads your recipes, batches and blends, and does its math the same way the app does.
          Changes arrive as a card you accept or reject, so we never apply anything behind your
          back.
        </p>
        {onJournal && (
          <p className="text-[11px] leading-snug text-muted-foreground">{JOURNAL_NOTE}</p>
        )}
        <div className="flex flex-col items-start gap-1 pt-1">
          {prompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => assistant.setDraft(prompt)}
              className="cursor-pointer rounded-md px-1.5 py-0.5 text-left text-[11px] text-primary hover:bg-accent/60"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>
      <RecentThreads />
    </>
  );
}

/**
 * Other conversations, offered where someone would look for them.
 *
 * Thread ids are derived from what the thread is about, so "switch thread" and
 * "open that page" are the same action — which is why this navigates rather than
 * swapping a transcript underneath an editor still showing something else. Only
 * on an empty thread: with a conversation on screen it would be clutter, and the
 * sidebar is already the way to a different recipe.
 *
 * A batch or a blend thread stores no `recipe_id`, so this list used to call
 * both of them "workspace" and send every click to `/recipes`. The id is the
 * column it was missing: `subjectOfThreadId` reads it back, and the destination
 * falls out with no migration behind it.
 */
function RecentThreads() {
  const assistant = useAssistant();
  const navigate = useNavigate();
  const [threads, setThreads] = useState<readonly AiThreadSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    void fetchThreads().then((loaded) => {
      if (!cancelled) setThreads(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const others = threads
    .filter((thread) => thread.threadId !== assistant.threadId && thread.messageCount > 0)
    .slice(0, 6);
  if (others.length === 0) return null;

  return (
    <section className="flex flex-col gap-0.5 px-1">
      <h3 className="px-1.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
        Earlier conversations
      </h3>
      {others.map((thread) => {
        const subject = subjectOfThreadId(thread.threadId);
        return (
          <button
            key={thread.threadId}
            type="button"
            className="flex cursor-pointer items-baseline gap-2 rounded-md px-1.5 py-1 text-left hover:bg-accent/60"
            onClick={() => {
              switch (subject.kind) {
                case 'recipe':
                  void navigate({
                    to: '/recipes/$recipeId',
                    params: { recipeId: subject.recipeId },
                  });
                  break;
                case 'batch':
                  void navigate({ to: '/batches/$batchId', params: { batchId: subject.batchId } });
                  break;
                case 'blend':
                  void navigate({ to: '/blending/$blendId', params: { blendId: subject.blendId } });
                  break;
                case 'global':
                  assistant.setRecipeId(null);
                  void navigate({ to: '/recipes' });
                  break;
              }
            }}
          >
            <span {...userContent} className="min-w-0 flex-1 truncate text-[11px]">
              {thread.title}
            </span>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              {THREAD_LABEL[subject.kind]}
            </span>
          </button>
        );
      })}
    </section>
  );
}

/** How long the control says "Copied" before it goes back to offering. */
const COPIED_FOR_MS = 1_500;

/**
 * Copy one answer — the text the assistant wrote, not the markup it was drawn as.
 *
 * The *source*: `markdown.tsx` renders four constructs and has no HTML sink, so
 * there is nothing in the rendering worth pasting that the source does not
 * already say, and the text a brewer puts in a forum post or a brew log should
 * be the text, bold marks and all.
 *
 * Quiet until it is wanted: `opacity-0` until the answer is hovered or
 * something inside it takes focus, which keeps a column of buttons out of a
 * conversation while leaving this one on the tab order — hidden is not the same
 * as unreachable, and a control nobody can reach by keyboard is not a control.
 *
 * Where there is no clipboard at all — plain HTTP, an old WebView — it is not
 * drawn rather than drawn broken. There is no fallback to select from here, and
 * that is deliberate: the answer is already selectable prose on the page, which
 * is the fallback the share dialog has to build an input for.
 */
function CopyAnswer({ text }: { readonly text: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FOR_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  if (typeof navigator === 'undefined' || navigator.clipboard?.writeText === undefined) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      // The label is the state: a screen reader hears "Copied" for the same
      // second and a half the eye sees the tick.
      aria-label={copied ? 'Copied' : 'Copy this answer'}
      className="text-muted-foreground opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
      onClick={() => {
        navigator.clipboard
          .writeText(text)
          .then(() => setCopied(true))
          .catch(() => {
            // Permission refused, or a clipboard that only looked present.
            // Saying nothing is better than a toast about a button nobody
            // pressed on purpose.
          });
      }}
    >
      {copied ? <CheckIcon /> : <ClipboardCopyIcon />}
    </Button>
  );
}

/** One button, two places: under a failed notice and under a failed send. */
function RetryButton({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <Button type="button" variant="outline" size="xs" onClick={onRetry}>
      Try again
    </Button>
  );
}

function Notice({
  icon,
  title,
  body,
  variant = 'default',
  action,
}: {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly body: React.ReactNode;
  readonly variant?: 'default' | 'warning';
  /** Drawn under the body — the way out of the state the notice describes. */
  readonly action?: (() => void) | undefined;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 rounded-lg border px-3 py-3',
        variant === 'warning' ? 'border-warning/40 bg-warning/5' : 'border-border bg-muted/40',
      )}
    >
      <p className="flex items-center gap-2 text-xs font-medium">
        <span className={variant === 'warning' ? 'text-warning' : 'text-muted-foreground'}>
          {icon}
        </span>
        {title}
      </p>
      <div className="text-[11px] leading-snug text-muted-foreground">{body}</div>
      {action !== undefined && (
        <div className="pt-0.5">
          <RetryButton onRetry={action} />
        </div>
      )}
    </div>
  );
}
